import React from 'react';
import { useRef, useState } from 'react';
import { OptumCCGWidgetInitializer } from '@uhg-ccg-proj/convenient-checkout-ui-v2/dist/widget/ccg-widget.min';

type WidgetInstance = ReturnType<typeof OptumCCGWidgetInitializer>;
type AppEnv = 'prod' | 'stage' | 'test' | 'dev';
type ContainerType = 'modal' | 'drawer' | 'inline';

function AppTestHarnessReactV2() {
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const widgetInstanceRef = useRef<WidgetInstance | null>(null);

  // Callback logs
  const [callbackLogs, setCallbackLogs] = useState<{ type: 'success' | 'error'; label: string; data: unknown; timestamp: string }[]>([]);
  const addLog = (type: 'success' | 'error', label: string, data: unknown) => {
    setCallbackLogs([{ type, label, data, timestamp: new Date().toLocaleTimeString() }]);
  };

  // Unmount control
  const [canUnmount, setCanUnmount] = useState(false);

  // User input states
  const [checkoutSessionId, setCheckoutSessionId] = useState('');
  const [appEnv, setAppEnv] = useState<AppEnv>('stage');
  const [containerType, setContainerType] = useState<ContainerType>('modal');
  const [secondaryContainerType, setSecondaryContainerType] = useState<'modal' | 'drawer' | undefined>(undefined);

  const handlePayClick = () => {
    if (!widgetContainerRef.current) {
      console.error('Cannot initialize widget: root container element not found.');
      return;
    }
    if (!widgetInstanceRef.current) {
      const containerConfig = containerType === 'inline' && secondaryContainerType
        ? { type: containerType, secondaryContainer: { type: secondaryContainerType } }
        : { type: containerType };
      widgetInstanceRef.current = OptumCCGWidgetInitializer({
        rootElement: widgetContainerRef.current,
        checkoutSessionId,
        appEnv,
        containerConfig,
        onSuccess: (data) => {
          console.log('EVENT: onSuccess triggered with data:', data);
          addLog('success', 'onSuccess', data);
          setCanUnmount(true);
        },
        onError: (error) => {
          console.error('EVENT: onError triggered with error:', error);
          addLog('error', 'onError', error);
          setCanUnmount(true);
        },
        onEvent: (event) => {
          console.log('EVENT: onEvent triggered:', event);
        },
      });
      widgetInstanceRef.current.render();
    }
  };

  const handleUnmountClick = () => {
    widgetInstanceRef.current?.unmount();
    widgetInstanceRef.current = null;
    setCheckoutSessionId('');
    setCanUnmount(false);
    setCallbackLogs([]);
  };

  React.useEffect(() => {
    return () => {
      widgetInstanceRef.current?.unmount();
    };
  }, []);

  return (
    <div id="bootstrapper-test-harness-container" style={{ padding: '24px 36px' }}>
      <header className="bootstrapper-test-harness-header">
        <p id="ccg-v2-version-badge" style={{ fontSize: '12px', color: '#888', margin: '0 0 16px 0' }}>
          @uhg-ccg-proj/convenient-checkout-ui v{__CCG_V2_VERSION__}
        </p>
        <h1>Widget Test Harness (v2)</h1>
        <p>This application tests the NPM package v2.</p>
        {/* User Input Form */}
        <form id="widget-config-form" style={{ marginBottom: 24 }}>
          <div id="checkout-session-id-field" style={{ marginBottom: 12 }}>
            <label id="checkout-session-id-label">
              Checkout Session ID:
              <input
                id="checkout-session-id-input"
                type="text"
                value={checkoutSessionId}
                onChange={e => setCheckoutSessionId(e.target.value)}
                style={{ marginLeft: 8, width: 300 }}
                placeholder="Enter session ID"
              />
            </label>
          </div>
          <div id="app-env-field" style={{ marginBottom: 12 }}>
            <label id="app-env-label">
              App Environment:
              <select
                id="app-env-select"
                value={appEnv}
                onChange={e => setAppEnv(e.target.value as AppEnv)}
                style={{ marginLeft: 8 }}
              >
                <option value="prod">prod</option>
                <option value="stage">stage</option>
                <option value="test">test</option>
                <option value="dev">dev</option>
              </select>
            </label>
          </div>
          <div id="container-type-field" style={{ marginBottom: 12 }}>
            <label id="container-type-label">
              Container Type:
              <select
                id="container-type-select"
                value={containerType}
                onChange={e => setContainerType(e.target.value as ContainerType)}
                style={{ marginLeft: 8 }}
              >
                <option value="modal">modal</option>
                <option value="drawer">drawer</option>
                <option value="inline">inline</option>
              </select>
            </label>
          </div>
          {containerType === 'inline' && (
            <div id="secondary-container-type-field" style={{ marginBottom: 12 }}>
              <label id="secondary-container-type-label">
                Secondary Container Type:
                <select
                  id="secondary-container-type-select"
                  value={secondaryContainerType ?? ''}
                  onChange={e => setSecondaryContainerType(e.target.value === '' ? undefined : e.target.value as 'modal' | 'drawer')}
                  style={{ marginLeft: 8 }}
                >
                  <option value="">secondary container type</option>
                  <option value="modal">modal</option>
                  <option value="drawer">drawer</option>
                </select>
              </label>
            </div>
          )}
        </form>

        <div id="action-buttons" style={{ display: 'flex', gap: 36, alignItems: 'center' }}>
          <button
            id="pay-remaining-balance-button"
            onClick={handlePayClick}
            disabled={canUnmount}
            className="pay-button"
            style={{ padding: '12px 16px', fontSize: '14px', backgroundColor: '#6b6', border: '1px solid #6b6', color: '#ffffff' }}
          >
            PAY REMAINING BALANCE
          </button>
          <button
            id="unmount-widget-button"
            onClick={handleUnmountClick}
            disabled={!canUnmount}
            style={{ padding: '12px 16px', fontSize: '14px', backgroundColor: '#808080', border: '1px solid #808080', color: '#ffffff', cursor: canUnmount ? 'pointer' : 'not-allowed', opacity: canUnmount ? 1 : 0.5 }}
          >
            UNMOUNT WIDGET
          </button>
        </div>
      </header>
      <main>
        <div id="bootstrapper-widget-container" ref={widgetContainerRef}></div>
      </main>
      <br />
      <hr />
      <div id="callback-logs-container" style={{ padding: 16, fontFamily: 'monospace', fontSize: '13px' }}>
        <strong>Callback Logs:</strong>
        {callbackLogs.length === 0 ? (
          <p style={{ color: '#999' }}>No callbacks fired yet.</p>
        ) : (
          callbackLogs.map((log, i) => (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ color: log.type === 'success' ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                [{log.timestamp}] {log.label}
              </div>
              <pre id={`callback-log-data-v2-${log.label}`} style={{ margin: '4px 0 0 0', padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4, overflowX: 'auto', fontSize: 12 }}>
                {JSON.stringify(log.data, null, 2)}
              </pre>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AppTestHarnessReactV2;
